import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { BasicTable } from '../../components/basic-table';
import { Button } from '../../components/ui/button';
import { TimeInterval } from '@cotera/api';
import { useOrgNamesLazy } from './hooks/use-org-names';
import { atcQueryKeys } from './query-keys';
import { CopyButton } from './components/copy-button';

interface StackFramesTableProps {
  timeInterval: TimeInterval;
}

// Helper to check if query should be opt-in (> 12 hours)
const isLargeTimeRange = (timeInterval: TimeInterval): boolean => {
  return timeInterval === '24h' || timeInterval === '48h';
};

export const StackFramesTable: React.FC<StackFramesTableProps> = ({
  timeInterval,
}) => {
  const client = useUntenantedClient();
  const [enableQuery, setEnableQuery] = useState(
    !isLargeTimeRange(timeInterval)
  );

  // Reset enableQuery when timeInterval changes
  React.useEffect(() => {
    setEnableQuery(!isLargeTimeRange(timeInterval));
  }, [timeInterval]);

  const isLargeRange = isLargeTimeRange(timeInterval);
  const shouldFetchData = !isLargeRange || enableQuery;

  // Always call the query hook, but conditionally enable it
  const { data: stackFramesData, isLoading } = useQuery({
    queryFn: async () => {
      const res = await client.atcVisibility.getStackFramesBreakdown({
        timeInterval,
      });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.stackFramesBreakdown(timeInterval),
    enabled: shouldFetchData,
  });

  // Extract unique org IDs - always call this hook
  const orgIds = useMemo(
    () =>
      stackFramesData !== undefined
        ? [...new Set(stackFramesData.map((row) => row.orgId))]
        : [],
    [stackFramesData]
  );

  const orgMap = useOrgNamesLazy(orgIds, orgIds.length > 0);

  // Enrich stack frames data with organization names
  const data = useMemo(
    () =>
      stackFramesData !== undefined
        ? stackFramesData.map((row) => ({
            ...row,
            organizationName: orgMap?.get(row.orgId) ?? 'Unknown',
          }))
        : [],
    [stackFramesData, orgMap]
  );

  type StatusByStream = NonNullable<typeof data>[number];

  const columns = useMemo<ColumnDef<StatusByStream>[]>(
    () => [
      {
        accessorKey: 'organizationName',
        header: 'Organization',
        cell: (info) => (
          <span className="inline-flex items-center gap-1.5">
            <span className="font-medium text-gray-900">
              {info.getValue() as string}
            </span>
            <CopyButton
              value={info.row.original.orgId}
              title={`Copy org ID: ${info.row.original.orgId}`}
            />
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'entity',
        header: 'Entity',
        cell: (info) => (
          <span className="font-mono text-xs text-gray-700">
            {(info.getValue() as string | null) ?? 'N/A'}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'key',
        header: 'Key',
        cell: (info) => (
          <span className="font-mono text-xs text-gray-700">
            {(info.getValue() as string | null) ?? 'N/A'}
          </span>
        ),
        enableColumnFilter: true,
      },
      {
        accessorKey: 'totalFailed',
        header: 'Failed',
        cell: (info) => {
          const value = info.getValue() as number;
          return value > 0 ? (
            <span className="font-semibold text-red-600">{value}</span>
          ) : (
            <span>{value}</span>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: 'percFailed',
        header: '% Failed',
        cell: (info) => `${(info.getValue() as number).toFixed(1)}%`,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'totalPending',
        header: 'Pending',
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'totalNoResultMessage',
        header: 'No Result Msg',
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'totalNoStackFrameEvents',
        header: 'No SF Events',
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'totalUnfinished',
        header: 'Incomplete',
        cell: (info) => info.getValue() as number,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'percUnfinished',
        header: '% Incomplete',
        cell: (info) => (
          <span className="text-gray-700">
            {`${(info.getValue() as number).toFixed(1)}%`}
          </span>
        ),
        enableColumnFilter: false,
      },
      {
        accessorKey: 'totalRun',
        header: 'Total Runs',
        cell: (info) => (
          <span className="font-medium">{info.getValue() as number}</span>
        ),
        enableColumnFilter: false,
      },
    ],
    []
  );

  // Now we can do conditional rendering - all hooks have been called
  // If large range and query not enabled, show the opt-in button
  if (isLargeRange && !enableQuery) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">
          ATC Run Status by Stream
        </h3>
        <div className="flex items-center justify-center p-8 bg-gray-50 border border-gray-200 rounded-md">
          <Button onClick={() => setEnableQuery(true)}>
            Run Postgres Query
          </Button>
        </div>
      </div>
    );
  }

  // If loading or no data yet, show loading state
  if (isLoading || stackFramesData === undefined) {
    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-700">
          ATC Run Status by Stream
        </h3>
        <div className="flex items-center justify-center p-8 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-700">
        ATC Run Status by Stream
      </h3>
      <p className="text-xs text-gray-500">
        Showing streams with incomplete runs, sorted by total incomplete count
      </p>
      <BasicTable
        data={data}
        columns={columns}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={false}
      />
    </div>
  );
};
